import { useEffect, useState } from 'react'
import { gqlFetchList } from 'app/lib/graphqlClient'
import type { PmsGate } from 'app/types/pms'
import { isLocationIntegrated, useUser } from 'app/utils'
import { PMS_CAPABILITY_FIELDS, pmsGate } from 'lib/pmsCapabilities'
import { logError } from 'lib/logging'

type PmsIntegration = {
  isIntegrated: boolean | undefined
  locationCount: number | undefined
  treatmentGate: PmsGate | undefined
  scheduleGate: PmsGate | undefined
}

const UNKNOWN: PmsIntegration = {
  isIntegrated: undefined,
  locationCount: undefined,
  treatmentGate: undefined,
  scheduleGate: undefined,
}

const ORG_LEVEL: PmsIntegration = {
  isIntegrated: true,
  locationCount: undefined,
  treatmentGate: `available`,
  scheduleGate: `available`,
}

/**
 * Whether the workspace has any PMS-integrated location, and how Unscheduled Treatment and the
 * schedule board are gated across its locations. Org level counts as integrated.
 */
export function usePmsIntegration(): PmsIntegration {
  // Keyed on the session's workspace id, not the workspace record: a switch replaces the user
  // first, and until the record follows, the old one would still answer for the new workspace.
  const user = useUser()
  const workspaceId = user ? (user.workspaceId ?? null) : undefined
  const [read, setRead] = useState<{ workspaceId: string; result: PmsIntegration }>()

  useEffect(() => {
    if (!workspaceId) return
    let current = true
    // The locations query sends the same variables in every workspace, so a cached answer
    // would be the previous workspace's.
    gqlFetchList(`locations`, {}, `id ${PMS_CAPABILITY_FIELDS}`, { requestPolicy: `network-only` })
      .then((data) => {
        if (!current) return
        setRead({
          workspaceId,
          result: {
            isIntegrated: data.hits.some(isLocationIntegrated),
            locationCount: data.hits.length,
            treatmentGate: pmsGate(data.hits, `unscheduledTreatment`),
            scheduleGate: pmsGate(data.hits, `scheduleBoard`),
          },
        })
      })
      .catch((err) => {
        if (!current) return
        // Locked rather than unknown, so the page shows the same locked state as the nav row.
        logError(`Could not read whether this workspace has a PMS integration`, err)
        setRead({
          workspaceId,
          result: { isIntegrated: false, locationCount: undefined, treatmentGate: `padlock`, scheduleGate: `padlock` },
        })
      })
    return () => {
      current = false
    }
  }, [workspaceId])

  if (workspaceId === null) return ORG_LEVEL
  return read && read.workspaceId === workspaceId ? read.result : UNKNOWN
}
